import type { TierGaugeTier } from "@/components/charts/TierGauge"

// Provisional bands (integrated LUFS). The "Normal" band sits lower than
// broadcast norms because telephony speech typically measures quieter than
// studio content; it brackets the -23 LUFS normalization target.
// Calibrate against real reviewer data before treating as fixed.
export const GAUGE_MIN_LUFS = -40
export const GAUGE_MAX_LUFS = 0
export const NORMAL_LOW_LUFS = -30
export const NORMAL_HIGH_LUFS = -18
const LOUD_LUFS = -10
const QUIET_LUFS = -36

/** Comparison target for the deep-dive distance label (EBU R128). */
export const TARGET_LUFS = -23

export function loudnessTier(lufs: number): TierGaugeTier {
  if (lufs > LOUD_LUFS) {
    return {
      label: "Too Loud",
      variant: "danger",
      blurb: "Well above speech norms — clipping and listener fatigue risk.",
    }
  }
  if (lufs > NORMAL_HIGH_LUFS) {
    return {
      label: "Loud",
      variant: "warning",
      blurb: "Louder than typical call audio.",
    }
  }
  if (lufs >= NORMAL_LOW_LUFS) {
    return {
      label: "Normal",
      variant: "success",
      blurb: "Within the typical loudness range for call audio.",
    }
  }
  if (lufs >= QUIET_LUFS) {
    return {
      label: "Quiet",
      variant: "warning",
      blurb: "Quieter than typical call audio.",
    }
  }
  return {
    label: "Too Quiet",
    variant: "danger",
    blurb: "Near the noise floor — listeners will strain to hear.",
  }
}

/** Human-readable distance from the comparison target, e.g. "4.2 LU below the -23 LUFS target". */
export function targetDeltaLabel(
  lufs: number,
  targetLufs: number = TARGET_LUFS,
): string {
  const delta = lufs - targetLufs
  if (Math.abs(delta) < 0.05) {
    return `at the ${targetLufs} LUFS target`
  }
  const direction = delta > 0 ? "above" : "below"
  return `${Math.abs(delta).toFixed(1)} LU ${direction} the ${targetLufs} LUFS target`
}

// Mirrors the backend's fixed outlier threshold (windows >= this far from the
// integrated loudness are emitted as subvalues).
export const OUTLIER_DEVIATION_LU = 8

// rgb triplets for waveform outlier regions (alpha applied at render).
export const LOUD_OUTLIER_FILL = "239, 68, 68" // red-500
export const QUIET_OUTLIER_FILL = "59, 130, 246" // blue-500
